import { MAX_NICKNAME_LENGTH } from "@clubap/shared";
import { useState } from "react";
import { requestPasswordReset, resetPassword, type Credentials } from "../api.js";

type Mode = "login" | "register";

interface Props {
  busy: boolean;
  error: string | null;
  /** Token que venía en `?reset=` — su presencia ES lo que abre esa pantalla. */
  resetToken: string | null;
  onSubmit: (mode: Mode, credentials: Credentials) => void;
  /** La contraseña ya cambió: el llamador limpia el token y avisa. */
  onResetDone: () => void;
}

export function AuthScreen({ busy, error, resetToken, onSubmit, onResetDone }: Props) {
  const [mode, setMode] = useState<Mode>("login");
  const [recovering, setRecovering] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [account, setAccount] = useState("");

  /*
   * Recuperar y restablecer llevan su PROPIO estado de ocupado y de error.
   *
   * `busy` y `error` son de la sesión: los pone el llamador al entrar o registrarse, y
   * su ciclo de vida está atado al de la conexión. Estos dos flujos no crean sesión —
   * son dos peticiones sueltas que empiezan y terminan acá— así que colgarlos del mismo
   * estado obligaría a subir a la app entera un problema que no sale de esta pantalla.
   */
  const [localBusy, setLocalBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const trimmed = username.trim();
  const canSubmit = trimmed.length >= 2 && password.length >= 8 && !busy;

  const run = async (action: () => Promise<void>): Promise<void> => {
    setLocalBusy(true);
    setLocalError(null);

    try {
      await action();
    } catch (cause) {
      setLocalError(cause instanceof Error ? cause.message : "Algo falló.");
    } finally {
      setLocalBusy(false);
    }
  };

  /*
   * Recuperar y restablecer NO son pestañas, y por eso son returns tempranos.
   *
   * "Entrar" y "Crear cuenta" son una elección que se ofrece de entrada; a recuperar se
   * llega desde un problema, y a restablecer sólo se llega desde un enlace del correo.
   * Ponerlos de pestaña los presentaría como caminos equivalentes y llenaría de ruido la
   * pantalla que casi siempre sólo necesita dos campos.
   *
   * El orden importa: el enlace del correo gana sobre lo que el usuario tuviera abierto.
   */

  // ---------------------------------------------------------------- restablecer

  if (resetToken !== null) {
    return (
      <div className="overlay">
        <form
          className="panel auth"
          onSubmit={(event) => {
            event.preventDefault();
            if (password.length < 8 || localBusy) return;
            void run(async () => {
              await resetPassword(resetToken, password);
              onResetDone();
            });
          }}
        >
          <h1 className="auth__title">ClubAP</h1>
          <h2 className="auth__subtitle">Contraseña nueva</h2>

          <label className="auth__field">
            <span>Contraseña</span>
            <input
              className="join__input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              autoFocus
              disabled={localBusy}
            />
          </label>

          <p className="auth__hint">
            Mínimo 8 caracteres. Al cambiarla se cierran todas las sesiones abiertas de tu
            cuenta, incluida la de quien haya entrado sin permiso.
          </p>

          <button className="btn" type="submit" disabled={password.length < 8 || localBusy}>
            {localBusy ? "Un momento…" : "Cambiar contraseña"}
          </button>

          {localError && (
            <p className="auth__error" role="alert">
              {localError}
            </p>
          )}
        </form>
      </div>
    );
  }

  // ------------------------------------------------------------------ recuperar

  if (recovering) {
    return (
      <div className="overlay">
        <form
          className="panel auth"
          onSubmit={(event) => {
            event.preventDefault();
            if (account.trim().length === 0 || localBusy) return;
            void run(async () => {
              await requestPasswordReset(account.trim());
              setSent(true);
            });
          }}
        >
          <h1 className="auth__title">ClubAP</h1>
          <h2 className="auth__subtitle">Recuperar cuenta</h2>

          {sent ? (
            /*
             * El mismo mensaje exista la cuenta o no.
             *
             * El servidor responde igual en los dos casos para que esto no sea un
             * detector de cuentas, y decir acá "no existe" volvería a abrir esa fuga por
             * delante. La frase es incómoda a propósito: es la que no revela nada.
             */
            <>
              <p className="auth__hint">
                Si esa cuenta existe y tiene un correo confirmado, le acaba de llegar un
                enlace. Vence en una hora y sirve una sola vez.
              </p>
              <p className="auth__hint">
                ¿No llegó? Revisa el correo no deseado. Si nunca confirmaste una dirección,
                esta vía no puede ayudarte — escríbele a quien administra el juego.
              </p>
            </>
          ) : (
            <>
              <label className="auth__field">
                <span>Tu correo o tu nombre de cuenta</span>
                <input
                  className="join__input"
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  autoComplete="username"
                  autoFocus
                  disabled={localBusy}
                />
              </label>

              <p className="auth__hint">
                Te mandamos un enlace para poner una contraseña nueva. Sólo funciona si tu
                cuenta tiene un correo confirmado.
              </p>

              <button
                className="btn"
                type="submit"
                disabled={account.trim().length === 0 || localBusy}
              >
                {localBusy ? "Enviando…" : "Enviar enlace"}
              </button>
            </>
          )}

          {localError && (
            <p className="auth__error" role="alert">
              {localError}
            </p>
          )}

          <button
            className="auth__link"
            type="button"
            onClick={() => {
              setRecovering(false);
              setSent(false);
              setLocalError(null);
            }}
          >
            Volver
          </button>
        </form>
      </div>
    );
  }

  // ------------------------------------------------------------ entrar / crear

  return (
    <div className="overlay">
      <form
        className="panel auth"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          onSubmit(mode, {
            username: trimmed,
            password,
            // Vacío significa "sin correo", no cadena vacía: el servidor distingue no
            // haberlo dado de haberlo dado mal, y sólo lo segundo rechaza el registro.
            ...(mode === "register" && email.trim() ? { email: email.trim() } : {}),
          });
        }}
      >
        <h1 className="auth__title">ClubAP</h1>

        <div className="auth__tabs" role="tablist">
          <button
            className={`auth__tab${mode === "login" ? " auth__tab--on" : ""}`}
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            onClick={() => setMode("login")}
          >
            Entrar
          </button>
          <button
            className={`auth__tab${mode === "register" ? " auth__tab--on" : ""}`}
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            onClick={() => setMode("register")}
          >
            Crear cuenta
          </button>
        </div>

        <label className="auth__field">
          <span>Nombre</span>
          <input
            className="join__input"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            maxLength={MAX_NICKNAME_LENGTH}
            autoComplete="username"
            autoFocus
            disabled={busy}
          />
        </label>

        <label className="auth__field">
          <span>Contraseña</span>
          <input
            className="join__input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            /* Le dice al gestor de contraseñas si guardar una nueva o rellenar. */
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            disabled={busy}
          />
        </label>

        {mode === "register" && (
          <label className="auth__field">
            <span>Correo (opcional)</span>
            <input
              className="join__input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              inputMode="email"
              disabled={busy}
            />
          </label>
        )}

        <p className="auth__hint">
          {mode === "register"
            ? "Mínimo 8 caracteres. El correo sólo sirve para recuperar tu cuenta si olvidas la contraseña; puedes agregarlo después."
            : "Tu tortuga y tus monedas te esperan."}
        </p>

        <button className="btn" type="submit" disabled={!canSubmit}>
          {busy ? "Un momento…" : mode === "login" ? "Entrar" : "Crear cuenta"}
        </button>

        {error && (
          <p className="auth__error" role="alert">
            {error}
          </p>
        )}

        {mode === "login" && (
          <button className="auth__link" type="button" onClick={() => setRecovering(true)}>
            ¿Olvidaste tu contraseña?
          </button>
        )}
      </form>
    </div>
  );
}
